import './AsyncSearch.css';
import './../input.scss';
import React, { useState, useEffect, useRef } from 'react';
import classnames from 'classnames';
import { SelectableList } from '../../SelectableList/SelectableList';
import { getProductName, Product } from '../../../model/product';
import { Event, getEventName } from '../../../model/event';
import { Icon } from '../../Icon/Icon';
import { IconName } from '../../Icon/IconName';
import { fullUserName, User } from '../../../model/user';
import { searchCollection, searchElementById, OptionType, useRemoteSearch, Collection } from '../../../model/data';
import { handleError } from '../../../model/notification';
import { t } from '../../../locales/translate';
import { useErrorHandler } from '../../../util/util';
import { getVenueName, Venue } from '../../../model/venue';
import { Tooltip } from '../../Tooltip/Tooltip';
import * as PopperJS from 'popper.js';
import { FormField } from '../../../model/form';
import { getCategoryName } from '../../../model/category';
import { JSONObject } from 'k6';
import { Collapse } from '@nerdgeschoss/shimmer-component-collapse';
import { useId } from '../../../util/react-util';
import { getShopNameWithDomain } from '../../../model/shop';

interface Props extends FormField<string> {
  actions?: React.ReactNode | React.ReactNodeArray;
  searchIn: OptionType[];
  required?: boolean;
  position?: PopperJS.Placement;
  maxResults?: number;
  minQueryLength?: number;
  description?: string;
  icon?: IconName;
  isLoading?: boolean;
  errorOverride?: string;
  hiddenValues?: string[];
  additionalParams?: JSONObject;
  loadingMessage?: string;
  onChangeObject?: (value: Collection | null) => void;
}

export function FormSearch({
  name,
  value,
  label,
  actions,
  searchIn,
  minQueryLength = 2,
  maxResults = 10,
  icon,
  errorOverride,
  isLoading,
  description,
  position = 'bottom-start',
  disabled,
  inputId,
  required,
  errors,
  hiddenValues,
  additionalParams,
  touched,
  valid,
  onChange,
  onBlur,
  onChangeObject,
}: Props): JSX.Element {
  const [pendingValue, setPendingValue] = useState('');
  const [selectedOption, setSelectedOption] = useState<Collection | null>(null);
  const [active, setActive] = useState(false);
  const parentRef = useRef<HTMLDivElement>(null);
  const [initialLoading, setInitialLoading] = useState(false);
  const [search, searchLoading, error] = useRemoteSearch(
    pendingValue,
    async (term, additionalParams) =>
      searchCollection({ term, types: searchIn, additionalParams, perPage: maxResults }),
    minQueryLength,
    additionalParams
  );
  const loading = searchLoading || isLoading || initialLoading;
  const id = useId(inputId);

  function getOptionLabel(
    option: Collection & {
      itemType?: string;
      sku?: string;
      email?: string;
      name?: string | null;
      domain?: string | null;
    }
  ): string {
    switch (option.itemType) {
      case 'product':
        return `${option.sku} - ${getProductName(option as Product)}`;
      case 'event':
        return getEventName(option as Partial<Event>, {
          sku: true,
          venueName: true,
          city: true,
          happeningFrom: true,
        });
      case 'user':
        return [fullUserName(option as User), option.email].filter(Boolean).join('    ');
      case 'venue':
        return getVenueName(option as Venue, { address: true });
      case 'category':
        return (
          getCategoryName(option.id, {
            withShop: true,
            includeShopDomain: true,
            preferInternalName: true,
          }) || option.id
        );
      case 'shop':
        return getShopNameWithDomain(option);
      default:
        return option.name || option.id;
    }
  }

  async function getInitialValue(): Promise<void> {
    setInitialLoading(true);
    try {
      const option = await searchElementById(value as string, searchIn);
      if (option) {
        setPendingValue(getOptionLabel(option) || '');
        setSelectedOption(option);
      }
    } catch (error) {
      handleError(error);
    }
    setInitialLoading(false);
  }

  useEffect(() => {
    if (value) {
      getInitialValue();
    } else {
      handleOnClear();
    }
  }, [value]);

  useErrorHandler(error);

  function handleOnSelect(id: string): void {
    setSelectedOption(null);
    if (!loading && search) {
      const option = search.results.find((item) => item.id === id);
      if (option) {
        setSelectedOption(option);
        setPendingValue(getOptionLabel(option) || pendingValue);
      }
      setActive(false);
    }
  }

  function handleOnClear(): void {
    setPendingValue('');
    setActive(false);
    setSelectedOption(null);
    onChange?.(undefined);
    onChangeObject?.(null);
  }

  useEffect(() => {
    if (selectedOption) {
      onChangeObject?.(selectedOption);
      onChange?.(selectedOption.id);
    }
  }, [selectedOption]);

  function renderOptions(): (React.ReactNode | undefined | null)[] {
    if (search) {
      return search.results
        .filter((v) => !hiddenValues?.find((id) => v.id === id))
        .map((item) => {
          return (
            <div key={item.id} onClick={() => handleOnSelect(item.id)}>
              {getOptionLabel(item)}
            </div>
          );
        });
    }
    return [];
  }

  // Event Handlers
  async function handleOnChange(e: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    setPendingValue(e.currentTarget.value);
  }

  function handleOnBlur(): void {
    if (selectedOption) {
      return setPendingValue(getOptionLabel(selectedOption));
    }
    if (pendingValue === '') {
      handleOnClear();
    }
    if (pendingValue && !selectedOption && !active) {
      handleOnClear();
    }
    onBlur?.();
  }

  function renderInput(): JSX.Element {
    return (
      <div className="async-search__input-container">
        <input
          className="input__input"
          type="text"
          id={id}
          name={name}
          placeholder={initialLoading ? `${t.actions.loading('')}...` : undefined}
          value={pendingValue}
          autoComplete="off"
          onChange={handleOnChange}
          disabled={disabled}
          onBlur={handleOnBlur}
        />
        <Icon
          className={classnames('async-search__loading', {
            'async-search__loading--active': loading,
          })}
          name="search--sm"
          loading={loading}
        />
      </div>
    );
  }

  const displayErrors = touched && !valid;

  return (
    <div className="input async-search" ref={parentRef}>
      {icon && <Icon name={icon} className="input__icon" />}
      <div
        className={classnames('input__container', {
          'input__container--active': active,
          'input__container--full': (value !== undefined && value !== null) || active || pendingValue,
        })}
      >
        <Tooltip
          style={{ width: parentRef.current?.clientWidth }}
          open={!disabled && active}
          onChange={(open) => {
            if (!disabled) {
              setActive(open);
            }
          }}
          rounded={false}
          gap="none"
          arrow={false}
          position={position}
          mode="click"
          reference={
            <div className="input__input-wrapper">
              {label && (
                <label className="input__label" htmlFor={id}>
                  {label}
                  {!required && ` (${t.general.optional})`}
                </label>
              )}
              <>
                {renderInput()}
                {!disabled && pendingValue && (
                  <Icon className="async-search__clear" name="remove" onClick={handleOnClear}></Icon>
                )}
              </>
              {actions && <div className="input__actions">{actions}</div>}
            </div>
          }
        >
          <SelectableList
            className="async-search__select-menu-items"
            unSelectableFooter={
              search && search.total > maxResults ? (
                <span className="async-search__more-results">{t.errors.moreResults(search.total - maxResults)}</span>
              ) : undefined
            }
            options={renderOptions()}
          />
        </Tooltip>
        <div className="input__description-wrapper">
          {description && <div className="input__description">{description}</div>}
          {errorOverride ? (
            <div className="input__error-message">{errorOverride}</div>
          ) : (
            <Collapse open={displayErrors}>
              {errors?.map((error) => {
                return (
                  <div className="input__error-message" key={error}>
                    {t.errors[error] || error}
                  </div>
                );
              })}
            </Collapse>
          )}
        </div>
      </div>
    </div>
  );
}
